// @flow
import React, { Component, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { ReactReduxContext } from 'react-redux';
import safelyHoistStatics from '../../helpers/safelyHoistStatics';
import { isLoaded as isAuthLoaded } from '../../stores/auth';
import ssRedirect from '../Common/SSRedirect';
import { isFeatureEnabled } from '../../stores/features';

declare var __SERVER__: boolean;

const debug = require('debug')('jp:withAuthentication');

const buildRedirectUrl = (history, config) => {
  const path = encodeURIComponent(
    `${history.location.pathname}${history.location.search}`
  );
  const extraParams =
    config && config.urlParams ? `&${config.urlParams()}` : '';
  return `/auth/login/?next=${path}&is_temp=1${extraParams}`;
};

/**
 * All withAuthentication to be disabled based on a feature flag
 * Used for A/B testing on the guest checkout
 * @param params
 * @param store
 * @returns {boolean}
 */
const requireAuthDisabledForRoute = (params, store) => {
  const routeType = get(params, 'route.meta.routeType');

  return isFeatureEnabled(
    store.getState().features,
    `disable_require_auth_${routeType}`,
    'on'
  );
};

const checkAuthentication = (params, store, config) => {
  if (requireAuthDisabledForRoute(params, store)) {
    return false;
  }

  const { history } = params;
  const currentPath = history.location.pathname || '';
  const shouldRedirect =
    !isAuthLoaded(store.getState()) && currentPath.indexOf('auth') === -1;
  debug('checkAuthentication', { shouldRedirect, ...history.location });
  if (shouldRedirect) {
    history.push(buildRedirectUrl(history, config));
  }
  return shouldRedirect;
};
/**
 * HOC to wrap restricted pages
 */
export default config => BaseComponent => {
  type Props = {
    location: any,
    store: any,
    originalProps: any
  };

  class Restricted extends Component<Props> {
    authFailed: boolean;

    constructor(props: Props) {
      super(props);

      if (!props.store) {
        // Throw an error if the store is not available to us
        throw new Error(
          'This component can only be used on a Container component!'
        );
      }

      this.authFailed = false;
    }

    componentWillMount() {
      this.authFailed = checkAuthentication(
        this.props,
        this.props.store,
        config
      );
    }

    componentWillReceiveProps(nextProps) {
      if (nextProps.location !== this.props.location) {
        this.authFailed = checkAuthentication(
          nextProps,
          this.props.store,
          config
        );
      }
    }

    render() {
      if (this.authFailed && __SERVER__) {
        return ssRedirect(buildRedirectUrl(this.props.originalProps), config);
      }

      if (!this.authFailed) {
        return <BaseComponent {...this.props.originalProps} />;
      }

      return null;
    }
  }

  const displayName =
    BaseComponent.displayName || BaseComponent.name || 'Component';

  Restricted.displayName = `Restricted[${displayName}]`;

  safelyHoistStatics(Restricted, BaseComponent);

  const WithStore = props => {
    const store = useContext(ReactReduxContext);

    return React.createElement(Restricted, {
      originalProps: props,
      ...props,
      ...store
    });
  };

  return withRouter(WithStore);
};
